import { appendRemainingUrl } from "@/shared/thorlabs/shared/functions/appendRemainingUrl";
import type { IContent, IContentAreaItem, IExpanded, IImage } from "@/shared/thorlabs/shared";

export interface IHtmlBlock extends IContent {
  htmlContent: string;
}

export interface IVirtoHtmlBlock extends IHtmlBlock {}

export interface ITabContainerBlock extends IContent {
  content: IContentAreaItem<IContent>[];
}

export interface IResponsiveImageBlock extends IContent {
  desktopImage: IImage;
  tabletImage: IImage;
  mobileImage: IImage;
}

export interface IFooterCurrencyPage {
  id: number;
  workId: number;
  guidValue: string;
  url: string;
  providerName: string | null;
  href: string;
  text: string;
  target: string;
}

export interface ISocialMediaLink {
  icon: string;
  link: string;
}

export interface ICompanyLink {
  iconImage: IImage | null;
  text: string;
  link: string;
}

export interface IAdditionalLink {
  displayName: string;
  link: string;
}

export interface IFooterCurrencyData {
  address: string;
  salesPhone: string;
  salesEmail: string;
  technicalPhone: string;
  technicalEmail: string;
  techSalesEmail: string;
  returnEmail: string;
  hoursLine1: string;
  hoursLine2: string;
  pages: IFooterCurrencyPage[];
  linkedPages: IFooterCurrencyPage[];
  companyLinks: ICompanyLink[];
  socialMediaLinks: ISocialMediaLink[];
  additionalLink: IAdditionalLink;
}

export interface IFooterBlock {
  name: string;
  eurData: IFooterCurrencyData;
  fraData: IFooterCurrencyData;
  sweData: IFooterCurrencyData;
  usdData: IFooterCurrencyData;
  cnyData: IFooterCurrencyData;
  gbpData: IFooterCurrencyData;
  sekData: IFooterCurrencyData;
  jpyData: IFooterCurrencyData;
  brlData: IFooterCurrencyData;
  inrData: IFooterCurrencyData;
  chinaDomainIdentifier: IAdditionalLink;
}

export interface IShippingMethodBlock {
  eurData: string;
  usdData: string;
  cnyData: string;
  gbpData: string;
  sekData: string;
  jpyData: string;
  brlData: string;
}

export interface ILogo {
  url: string;
}

export interface IVisualNavigationLayer {
  routeSegment: string;
  url: string;
  menuRemainingUrl?: string;
}

export interface IVisualNavigationLayerPageExpanded {
  expanded: {
    name: string;
    visNavTitle: string;
    type: string;
    labelColor: string;
    routeSegment: string;
    url: string;
    menuRemainingUrl?: string;
    rows: IContentLink[];
  };
}

export interface IMenuImage {
  url: string | null;
}

export interface IStandardPage {
  name: string;
  type: string;
  routeSegment: string | null;
  contentLink?: IContentLink; // Optional
  menuIcon?: IMenuImage; // Optional
  menuRemainingUrl?: string;
}

export type ExpandedTypes =
  | IMultiLevelMenuItemBlock
  | IMenuItemBlock
  | IVisualNavigationLayerPageExpanded
  | IStandardPage
  | IHtmlBlock
  | { name: string };

export interface IContentLink {
  expanded:
    | IMultiLevelMenuItemBlock
    | IMenuItemBlock
    | IVisualNavigationLayerPageExpanded
    | IStandardPage
    | IHtmlBlock
    | { name: string };
  url?: string | null; // Optional
}

export interface IMenuItemBlock {
  visualNavigationLayerPage: IVisualNavigationLayerPageExpanded;
  name: string;
  contentType: string[];
  items: IContentAreaItem<IContent>[];
  menuImage?: IMenuImage; // Optional
  menuIcon?: IMenuImage; // Optional
  rightColumnItems: IContentAreaItem<IContent>[];
}

export interface IMultiLevelMenuItemBlock {
  name: string;
  contentType: string[];
  visualNavigationLayer: { expanded: IVisualNavigationLayer };
  items: IContentAreaItem<IContent>[];
  secondaryItems: IContentAreaItem<IContent>[];
}

export interface IDesktopMenuItem {
  displayOption: string;
  contentLink: IContentLink;
}

export interface IMobileMenuItem {
  displayOption: string;
  tag: string | null;
  contentLink: IContentLink;
}

export interface IHeaderBlock {
  name: string;
  logo: ILogo;
  desktopMenuItems: IDesktopMenuItem[];
  mobileMenuItems: IMobileMenuItem[];
  banner: IContentAreaItem<IContent>[];
}

export interface IBannerBlock extends IContent {
  name: string;
  color: string;
  position: string;
  currency: string;
  text: string;
  saved: string;
  icon: {
    url: string | undefined;
  };
}

export interface IAdBlock extends IContent {
  name: string;
  startDate: Date;
  expirationDate: Date;
  destinationLink: string;
  smallAdImage: IImage;
  smallAdTitle: string;
  smallAdText: string;
  smallAdButtonText: string;
  largeAdImage: IImage;
  largeAdImage2: IImage;
  largeAdTitle: string;
  largeAdText: string;
  largeAdButtonText: string;
}

export interface IMenuItem {
  label: string;
  url: string;
  icon: string;
  menuTemplate: string;
  items: Array<IMenuSubItem>;
  rightColumnItems: Array<IMenuSubItem>;
  active?: boolean;
}

export interface IMenuSubItem {
  label: string;
  url: string;
  icon: string;
  image: string;
  items: Array<IMenuSubItem>;
  color: string;
  separator: boolean;
  active?: boolean;
  contentType?: string;
  showHeader?: boolean;
}

export interface IVisualNavigationStatsBlock extends IContent {
  newProductsInfo: string;
}

export interface ILinkBlockBase {
  linkText: string;
  target: string;
  linkType: string;
}

export interface IVisualNavigationButton extends IContent {
  name: string;
  title: string;
  labelColor: string;
  url: string;
}

export interface IRowBlock extends IContent {
  backgroundType: string;
  useSeparator: boolean;
}
export interface IProductCategoriesBlock extends IRowBlock {
  title: string;
  subtitle: string;
  categoryLinkRemainingUrl: string;
  categories: IContentAreaItem<IVisualNavigationButton>[];
  viewAllLink: ILinkBlockBase;
  customLink: ILinkBlockBase;
}

export interface IProductFamilyCardBlock extends IContent {
  title: string;
  category: string;
  image: IImage;
  linkText: string;
  target: IExpanded<IContent>;
  remainingUrl: string;
  linkType: string;
  youTubeLink: string;
  bilibiliLink: string;
}
export interface IProductsContainerBlock extends IRowBlock {
  title: string;
  subtitle: string;
  imageIcon: IImage;
  link: ILinkBlockBase;
  items: IContentAreaItem<IProductFamilyCardBlock>[];
}

export interface ITextBlocksSideBySideBlock extends IRowBlock {
  sectionTitle: string;
  title: string;
  titleMaxWidth: number;
  summary: string;
  customLink?: ILinkBlockBase;
  items: IContentAreaItem<IContent>[];
  itemsPosition: string;
  hasBorder: boolean;
  stickerImage: IImage;
}

export interface ISideBySideBlock extends IRowBlock {
  sectionTitle: string;
  title: string;
  summary: string;
  iconTextList: IImageTextBlock[];
  isVideo: boolean;
  youTubeLink: string;
  bilibiliLink: string;
  image: IImage;
  imagePosition: string;
  contentFill: string;
  containerFillSize: string;
  link: ILinkBlockBase;
  borderType: string;
}

export interface ITwoImagesBlock extends IRowBlock {
  sectionTitle: string;
  title: string;
  summary: string;
  image1: IImage;
  image2: IImage;
  url?: string;
}

export interface ITitleTextBlock extends IRowBlock {
  title: string;
  sections: IContentAreaItem<ITitleTextSectionBlock>[];
}

export interface ITitleTextSectionBlock extends IContent {
  leftSide: string;
  rightSide: string;
  showTitleOnTop: boolean;
  showTitleInRed: boolean;
}

export interface ITitleTextVariantBlock extends IRowBlock {
  sections: IContentAreaItem<ITitleTextVariantSectionBlock>[];
}

export interface ITitleTextVariantSectionBlock extends IContent {
  leftSideSectionTitle: string;
  leftSideTitle: string;
  leftSideParagraphs: IContentAreaItem<ITitleTextVariantSectionLeftParagraphBlock>[];
  rightSide: string;
  displaySeparator: boolean;
}

export interface ITitleTextVariantSectionLeftParagraphBlock extends IContent {
  icon: IImage;
  text: string;
}

export interface IGalleryBlock extends Partial<IContent> {
  galleryImages: IImage[];
}

export interface IImageTextBlock extends IContent {
  text: string;
  image: IImage;
}
export interface IAdditionalContentBlock extends IContent {
  title: string;
  subtitle: string;
  image: IImage;
  imageSize: string;
  disableImageBorder: boolean;
  content: string;
  link: ILinkBlockBase;
  disableBorderBox: boolean;
}
export interface IAdditionalContentContainerBlock extends IRowBlock {
  title: string;
  itemsPerRow: number;
  items: IContentAreaItem<IAdditionalContentBlock>[];
  containerFillSize: string;
  useBottomRedSeparator: boolean;
}

export interface ITextCardBlock extends IContent {
  title: string;
  summary: string;
  link: ILinkBlockBase;
}
export interface IImageCardBlock extends IContent {
  title: string;
  subtitle: string;
  image: IImage;
  imageHeight: string;
  content: string;
  link: ILinkBlockBase;
}
export interface IVideoCardBlock extends IContent {
  title: string;
  summary: string;
  badgeText: string;
  youTubeLink: string;
  bilibiliLink: string;
}
export interface ICardsContainerBlock extends IRowBlock {
  title: string;
  sectionTitle: string;
  summary: string;
  link: ILinkBlockBase;
  items: IContentAreaItem<IContent>[];
}

export interface IAlertBlock extends IContent {
  title: string;
  text: string;
  icon: IImage;
  type: string;
  saved: string;
  currency: string;
}

export interface IPunchoutAlertBlock extends IContent {
  title: string;
  text: string;
  icon: IImage;
  type: string;
}

export interface IPunchoutAlertContainerBlock extends IContent {
  alertList: IContentAreaItem<IPunchoutAlertBlock>[];
}

export interface IFeaturedTradeshowBlock extends IContent {
  titleSegments: string[];
  date: string;
  location: string;
  tradeshowImages: IResponsiveImageBlock;
  link: ILinkBlockBase;
}
export interface IFeaturedContentBlock extends IContent {
  title: string;
  sectionTitle: string;
  subtitle: string;
  isSubtitleCategory: boolean;
  featuredImages: IResponsiveImageBlock;
  content: string;
  badgeText: string;
  link: ILinkBlockBase;
  youTubeLink: string;
  bilibiliLink: string;
  displayOption: string;
  imagePositionOneThird: string;
  imagePositionTwoThirds: string;
}
export interface IFeaturedContainerBlock extends IRowBlock {
  items: IContentAreaItem<IContent>[];
}

export interface IGalleryCarousel {
  title: string;
  description: string;
  images: IImage[];
  buttonText: string;
  externalLink?: string;
}

export interface ITimelineBlock extends IRowBlock {
  title: string;
  steps: IContentAreaItem<ITimelineStepBlock>[];
}

export interface ITimelineStepBlock extends IContent {
  timeReference: string;
  title: string;
  summary: string;
  isOdd: boolean;
}

export interface ICollapsibleContainerBlock extends IRowBlock {
  title: string;
  collapsibleBlocks: IContentAreaItem<ICollapsibleBlock>[];
}

export interface ICollapsibleBlock extends IContent {
  collapsedText: string;
  expandedText: string;
}

export interface IPersonCardContainerBlock extends IContent {
  items: IContentAreaItem<IPersonCardBlock>[];
}

export interface IPersonCardBlock extends IContent {
  name: string;
  jobTitle: string;
  image: IImage;
}

export interface IPersonContactCardBlock extends IContent {
  name: string;
  jobTitle: string;
  image: IImage;
  email: string;
  phone: string;
}

export interface IPersonContactCardContainerBlock extends IContent {
  title?: string;
  summary?: string;
  items: IContentAreaItem<IPersonContactCardBlock>[];
}

export interface IPersonContactCardCurrencySpecificContainerBlock extends IContent {
  title?: string;
  summary?: string;
  usdPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  eurPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  gbpPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  sekPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  cnyPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  jpyPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
  brlPersonContactCardBlocks: IContentAreaItem<IPersonContactCardBlock>[];
}

export interface ITwoColumnsContainerBlock extends IRowBlock {
  leftColumn: IContentAreaItem<IPersonContactCardContainerBlock | ITitleTextSectionBlock>[];
  hideLeftColumnBorder: boolean;
  rightColumn: IContentAreaItem<IPersonContactCardContainerBlock | ITitleTextSectionBlock>[];
  hideRightColumnBorder: boolean;
}

export interface IBackgroundImageCardContainerBlock extends IContent {
  items: IContentAreaItem<IBackgroundImageCardBlock>[];
}
export interface IBackgroundImageCardBlock extends IContent {
  title: string;
  shortDescription: string;
  backgroundImage: IImage;
  target: string;
}

export interface ISimpleCallToActionBlock extends IContent {
  title: string;
  description: string;
  actionLink: ILinkBlockBase;
}

export interface ILocationBenefitBlock extends IContent {
  title: string;
  icon: IImage;
}

export function mapGraphQLDataToMenuItems(
  receivedMenuItems: IDesktopMenuItem[] | IMobileMenuItem[] | undefined,
  isMobile: boolean = false,
): IMenuItem[] {
  const menuItems: IMenuItem[] = [];

  // Iterate over the GraphQL data to extract and map properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receivedMenuItems?.forEach((item: IDesktopMenuItem | IMobileMenuItem | any) => {
    const expandedItem = item.contentLink.expanded;
    const menuTemplate = getMenuTemplate(expandedItem);
    const menuItem: IMenuItem = {
      label: expandedItem.visualNavigationLayerPage?.expanded?.name
        ? item.visualNavigationLayerPage?.expanded?.name
        : expandedItem.name,
      url: appendRemainingUrl(getUrlFromGraphQLItem(expandedItem), getMenuRemainingUrlFromGraphQLItem(expandedItem)),
      icon: expandedItem.menuIcon?.url || "",
      menuTemplate: menuTemplate,
      items: mapSubItems(expandedItem, menuTemplate, isMobile),
      rightColumnItems: menuTemplate === "two-columns" ? mapRightColumnItems(expandedItem, menuTemplate, isMobile) : [],
    };

    menuItems.push(menuItem);
  });

  processMenuItemsUrls(menuItems);
  return menuItems;
}

function getUrlFromGraphQLItem(item: ExpandedTypes): string {
  if (item && "visualNavigationLayer" in item && item.visualNavigationLayer?.expanded?.url) {
    return getUrlPathname(item.visualNavigationLayer.expanded.url);
  } else if (item && "visualNavigationLayerPage" in item && item.visualNavigationLayerPage?.expanded?.url) {
    return getUrlPathname(item.visualNavigationLayerPage.expanded.url);
  } else if (item && "link" in item && (item as Record<string, unknown>).link) {
    return getUrlPathname((item as Record<string, unknown>).link as string);
  } else if (item && "url" in item) {
    return getUrlPathname((item.url as string) || "");
  } else {
    return "";
  }
}

function getMenuRemainingUrlFromGraphQLItem(item: ExpandedTypes): string {
  if (item && "visualNavigationLayer" in item && item.visualNavigationLayer?.expanded?.menuRemainingUrl) {
    return item.visualNavigationLayer.expanded.menuRemainingUrl;
  } else if (
    item &&
    "visualNavigationLayerPage" in item &&
    item.visualNavigationLayerPage?.expanded?.menuRemainingUrl
  ) {
    return item.visualNavigationLayerPage.expanded.menuRemainingUrl;
  } else if (item && "menuRemainingUrl" in item) {
    return ((item as unknown as Record<string, unknown>).menuRemainingUrl as string) ?? "";
  }
  return "";
}

function getMenuTemplate(item: ExpandedTypes): string {
  if ("contentType" in item) {
    if (item.contentType[1] === "MultiLevelMenuItemBlock") {
      const menuItemBlock = item as IMultiLevelMenuItemBlock;
      return menuItemBlock.items?.length > 0 || menuItemBlock.secondaryItems?.length > 0 ? "three-columns" : "";
    } else if (item.contentType[1] === "MenuItemBlock") {
      const menuItemBlock = item as IMenuItemBlock;
      const hasItems = menuItemBlock.items?.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (child: any) =>
          child.contentLink?.expanded?.items?.length > 0 || child.contentLink?.expanded?.rightColumnItems?.length > 0,
      );
      return hasItems ? "two-columns" : "one-column";
    }
  }
  return "";
}

function mapSubItems(
  expandedItem: IMenuItemBlock | IMultiLevelMenuItemBlock,
  menuTemplate: string,
  isMobile: boolean,
): IMenuSubItem[] {
  const mappedSubItems: IMenuSubItem[] = [];
  // Check if items exist and map them
  if (expandedItem && "items" in expandedItem && expandedItem.items) {
    mappedSubItems.push(...mapItems(expandedItem.items, menuTemplate, isMobile));
  }

  // Check if rightColumnItems exist and map them
  if (
    expandedItem &&
    "rightColumnItems" in expandedItem &&
    expandedItem.rightColumnItems &&
    expandedItem.rightColumnItems.length > 0 &&
    (menuTemplate != "two-columns" || isMobile)
  ) {
    const rightColumnItems: IMenuSubItem[] = mapItems(expandedItem.rightColumnItems, menuTemplate, isMobile);
    mappedSubItems.push(...rightColumnItems);
  }

  // Check if secondaryItems exist and map them
  if (
    expandedItem &&
    "secondaryItems" in expandedItem &&
    expandedItem.secondaryItems &&
    expandedItem.secondaryItems.length > 0
  ) {
    const secondaryItems: IMenuSubItem[] = mapItems(expandedItem.secondaryItems, menuTemplate, isMobile, true);

    mappedSubItems.push(...secondaryItems);
  }

  return mappedSubItems;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRightColumnItems(expandedItem: any, menuTemplate: string, isMobile: boolean): IMenuSubItem[] {
  const mappedSubItems: IMenuSubItem[] = [];

  if (expandedItem.rightColumnItems && expandedItem.rightColumnItems.length > 0) {
    const rightColumnItems: IMenuSubItem[] = mapItems(expandedItem.rightColumnItems, menuTemplate, isMobile);

    mappedSubItems.push(...rightColumnItems);
  }

  return mappedSubItems;
}

function mapItems(
  menuItems: IContentAreaItem<IContent>[],
  menuTemplate: string,
  isMobile: boolean,
  isRightColumn: boolean = false,
): IMenuSubItem[] {
  const getImageUrl = (expandedItem: ExpandedTypes): string =>
    expandedItem && "menuImage" in expandedItem ? (expandedItem as IMenuItemBlock).menuImage?.url || "" : "";

  const getIconUrl = (expandedItem: ExpandedTypes): string =>
    expandedItem && "menuIcon" in expandedItem
      ? (expandedItem as IMenuItemBlock | IStandardPage).menuIcon?.url || ""
      : "";

  const getLabel = (expandedItem: ExpandedTypes): string => {
    if (expandedItem && "displayName" in expandedItem && (expandedItem as Record<string, unknown>).displayName) {
      return (expandedItem as Record<string, unknown>).displayName as string;
    }
    return expandedItem && "name" in expandedItem ? expandedItem.name : "";
  };

  const getSubItems = (expandedItem: ExpandedTypes): IMenuSubItem[] =>
    expandedItem && "items" in expandedItem && expandedItem.items
      ? mapSubItems(expandedItem as IMenuItemBlock | IMultiLevelMenuItemBlock, menuTemplate, isMobile)
      : [];

  const getColor = (expandedItem: ExpandedTypes): string =>
    expandedItem &&
    "visualNavigationLayerPage" in expandedItem &&
    expandedItem.visualNavigationLayerPage?.expanded?.labelColor
      ? expandedItem.visualNavigationLayerPage?.expanded.labelColor
      : "";

  return menuItems.map((subItem) => {
    const expandedItem = subItem.contentLink.expanded;
    const imageUrl = getImageUrl(expandedItem);
    const iconUrl = getIconUrl(expandedItem);
    const label = getLabel(expandedItem);
    const items = menuTemplate === "three-columns" && isMobile ? [] : getSubItems(expandedItem);
    const color = getColor(expandedItem);
    const separator =
      isRightColumn && (menuItems.indexOf(subItem) === 0 || menuItems.indexOf(subItem) === menuItems.length - 1);

    return {
      label: label,
      url: appendRemainingUrl(getUrlFromGraphQLItem(expandedItem), getMenuRemainingUrlFromGraphQLItem(expandedItem)),
      icon: iconUrl,
      image: imageUrl,
      items: items,
      color: color,
      separator: separator,
      contentType: subItem.contentLink.expanded.contentType?.at(-1),
      showHeader:
        expandedItem && "showHeader" in expandedItem
          ? ((expandedItem as Record<string, unknown>).showHeader as boolean)
          : undefined,
    };
  });
}

function getUrlPathname(urlString: string) {
  return !urlString || urlString === "" ? "/" : urlString;
}

export function processMenuItemsUrls(menuItems: IMenuItem[]): void {
  menuItems.forEach((menuItem) => {
    if (menuItem.menuTemplate === "two-columns") {
      menuItem.items.forEach((item) => {
        if (item.items.length === 0) {
          item.url = item.url === "" ? "/" : item.url;
        }
      });
    }
  });
}

export function shouldDisplaySubItemAsLabel(subItem: IMenuSubItem): boolean {
  return subItem.contentType === "MenuGroupBlock";
}
